import React, { useCallback, useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'sm_concierge_session_v1';

// Regex helpers to auto-detect lead data inside user messages
const RE_PHONE = /(\+?52\s*)?(\d{2,4}[\s-]?){2,3}\d{3,4}/;
const RE_EMAIL = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
const RE_NAME_HINT = /(?:soy|me llamo|mi nombre es|llamame|llámame)\s+([A-Za-zÁÉÍÓÚáéíóúÑñ]+(?:\s+[A-Za-zÁÉÍÓÚáéíóúÑñ]+){0,3})/i;

function extractLead(text) {
  const phoneMatch = text.match(RE_PHONE);
  const emailMatch = text.match(RE_EMAIL);
  const nameMatch = text.match(RE_NAME_HINT);
  return {
    phone: phoneMatch ? phoneMatch[0].trim() : '',
    email: emailMatch ? emailMatch[0].trim() : '',
    name: nameMatch ? nameMatch[1].trim() : '',
  };
}

// Mini-markdown renderer: bold (**), italic (*), inline code (`), line breaks.
// HTML-escapes first to prevent XSS, then converts safe tokens.
function MarkdownText({ text }) {
  if (!text) return null;
  const escape = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  let html = escape(text);
  // Bold first (greedy with non-greedy inside) — must run before italic
  html = html.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>');
  // Italic (single * not followed by *)
  html = html.replace(/(?<![*\w])\*([^*\n]+?)\*(?![*\w])/g, '<em>$1</em>');
  // Inline code
  html = html.replace(/`([^`]+?)`/g, '<code>$1</code>');
  // Line breaks
  html = html.replace(/\n/g, '<br/>');
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

/**
 * AI Sales Agent — Mónica
 * Floating bubble + chat panel, conversational lead capture (no explicit form).
 */
export default function ConciergeChat() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [leadSent, setLeadSent] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);
  const scrollRef = useRef(null);
  const msgRefs = useRef([]);
  const lastUserIdxRef = useRef(-1);
  const startedRef = useRef(false);
  const leadAccumRef = useRef({ name: '', phone: '', email: '' });

  // Smart scroll: when a new user message is added, position it near the top
  // so the user sees their question + the start of Mónica's reply.
  // During streaming we DO NOT auto-scroll to bottom (the user keeps context).
  useEffect(() => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;

    // Find latest user message index
    let latestUser = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') { latestUser = i; break; }
    }

    if (latestUser !== -1 && latestUser !== lastUserIdxRef.current) {
      // New user message → anchor it to the top of the visible area.
      lastUserIdxRef.current = latestUser;
      const el = msgRefs.current[latestUser];
      if (el) {
        // Use offset within the scroll container (avoid scrollIntoView jumping the page).
        const top = el.offsetTop - 8;
        container.scrollTo({ top, behavior: 'smooth' });
      }
    }
    // Update "more below" indicator: true only when the LAST real message bottom
    // is below the visible viewport (ignore the artificial bottom padding).
    requestAnimationFrame(() => {
      if (!container) return;
      const lastEl = msgRefs.current[messages.length - 1];
      if (!lastEl) { setHasMoreBelow(false); return; }
      const containerRect = container.getBoundingClientRect();
      const lastRect = lastEl.getBoundingClientRect();
      const more = lastRect.bottom > containerRect.bottom + 8;
      setHasMoreBelow(more);
    });
  }, [messages, open]);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const lastEl = msgRefs.current[messages.length - 1];
    if (!lastEl) { setHasMoreBelow(false); return; }
    const containerRect = container.getBoundingClientRect();
    const lastRect = lastEl.getBoundingClientRect();
    const more = lastRect.bottom > containerRect.bottom + 8;
    setHasMoreBelow(more);
  }, [messages.length]);

  const scrollToBottom = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        if (data?.session_id) {
          setSessionId(data.session_id);
          if (Array.isArray(data.messages)) setMessages(data.messages);
          if (data.leadAccum) leadAccumRef.current = data.leadAccum;
          if (data.leadSent) setLeadSent(true);
        }
      }
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.warn('[concierge] session restore failed', e);
    }
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      session_id: sessionId,
      messages,
      leadAccum: leadAccumRef.current,
      leadSent,
    }));
  }, [sessionId, messages, leadSent]);

  const startSession = useCallback(async () => {
    if (startedRef.current || sessionId) return;
    startedRef.current = true;
    try {
      const res = await fetch(`${API}/api/concierge/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source_page: window.location.pathname }),
      });
      const data = await res.json();
      setSessionId(data.session_id);
      setMessages([{ role: 'assistant', content: data.greeting, quickReplies: data.quick_replies || [] }]);
    } catch (e) {
      console.error('Agent start failed', e);
      setMessages([{ role: 'assistant', content: 'Hubo un problema iniciando el chat. Por favor recarga la página o escríbenos por WhatsApp.' }]);
    }
  }, [sessionId]);

  useEffect(() => {
    if (open && !sessionId) startSession();
    if (open) setHasUnread(false);
  }, [open, sessionId, startSession]);

  const sendFeedback = useCallback(async (messageIndex, rating) => {
    if (!sessionId) return;
    // Optimistic UI update
    setMessages((prev) => {
      const copy = [...prev];
      if (copy[messageIndex]) copy[messageIndex] = { ...copy[messageIndex], rating };
      return copy;
    });
    try {
      await fetch(`${API}/api/concierge/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message_index: messageIndex, rating }),
      });
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.warn('[concierge] feedback failed', err);
    }
  }, [sessionId]);

  const maybeCaptureLead = useCallback(async (userText) => {
    if (leadSent || !sessionId) return;
    const found = extractLead(userText);
    const accum = leadAccumRef.current;
    if (found.name && !accum.name) accum.name = found.name;
    if (found.phone && !accum.phone) accum.phone = found.phone;
    if (found.email && !accum.email) accum.email = found.email;
    leadAccumRef.current = accum;

    // Lead is "complete" when we have a name AND at least one channel (phone or email).
    const isComplete = accum.name && (accum.phone || accum.email);
    if (!isComplete) return;

    try {
      await fetch(`${API}/api/concierge/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          name: accum.name,
          phone: accum.phone,
          email: accum.email,
        }),
      });
      setLeadSent(true);
    } catch (e) {
      console.error('Auto lead capture failed', e);
    }
  }, [leadSent, sessionId]);

  const sendMessage = useCallback(async (text) => {
    const userText = text.trim();
    if (!userText || busy || !sessionId) return;
    setBusy(true);
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userText }, { role: 'assistant', content: '', streaming: true }]);

    // Fire-and-forget detection. Auto-end runs in parallel with the chat call.
    maybeCaptureLead(userText);

    try {
      const res = await fetch(`${API}/api/concierge/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: userText }),
      });
      if (!res.ok || !res.body) throw new Error('stream failed');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() || '';
        for (const p of parts) {
          if (!p.startsWith('data: ')) continue;
          try {
            const payload = JSON.parse(p.slice(6));
            if (payload.delta) {
              setMessages((prev) => {
                const copy = [...prev];
                const last = copy[copy.length - 1];
                if (last && last.streaming) {
                  copy[copy.length - 1] = { ...last, content: last.content + payload.delta };
                }
                return copy;
              });
            } else if (payload.done) {
              setMessages((prev) => {
                const copy = [...prev];
                const last = copy[copy.length - 1];
                if (last && last.streaming) {
                  copy[copy.length - 1] = { ...last, streaming: false, quickReplies: payload.quick_replies || [] };
                }
                return copy;
              });
            } else if (payload.error) {
              setMessages((prev) => [...prev.slice(0, -1), { role: 'assistant', content: 'Hubo un problema. Por favor inténtalo de nuevo.' }]);
            }
          } catch (parseErr) {
            if (process.env.NODE_ENV !== 'production') console.warn('[concierge] SSE parse failed', parseErr);
          }
        }
      }
    } catch (e) {
      console.error('Agent stream error', e);
      setMessages((prev) => [...prev.slice(0, -1), { role: 'assistant', content: 'Hubo un problema. ¿Puedes intentarlo de nuevo?' }]);
    } finally {
      setBusy(false);
    }
  }, [busy, sessionId, maybeCaptureLead]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          className="sm-concierge-fab"
          onClick={() => setOpen(true)}
          aria-label="Abrir chat con Mónica"
          data-testid="concierge-fab"
        >
          <span className="sm-concierge-fab-pulse" aria-hidden />
          <span className="icon-font" aria-hidden>auto_awesome</span>
          {hasUnread && <span className="sm-concierge-fab-dot" aria-hidden />}
          <span className="sm-concierge-fab-label">Habla con Mónica</span>
        </button>
      )}

      <div className={`sm-concierge-panel${open ? ' open' : ''}`} role="dialog" aria-label="Chat con Mónica" data-testid="concierge-panel">
        <header className="sm-concierge-head">
          <div className="sm-concierge-head-avatar" aria-hidden>
            <span className="icon-font">auto_awesome</span>
          </div>
          <div className="sm-concierge-head-meta">
            <strong>Mónica</strong>
            <span><span className="sm-concierge-status-dot" /> Asesora · en línea</span>
          </div>
          <button
            type="button"
            className="sm-concierge-close"
            onClick={() => setOpen(false)}
            aria-label="Cerrar chat"
            data-testid="concierge-close"
          >
            <span className="icon-font">close</span>
          </button>
        </header>

        <div className="sm-concierge-scroll" ref={scrollRef} onScroll={handleScroll}>
          {messages.length === 0 && (
            <div className="sm-concierge-empty">Iniciando…</div>
          )}
          {messages.map((m, i) => (
            <div
              key={i}
              ref={(el) => { msgRefs.current[i] = el; }}
              className={`sm-concierge-msg ${m.role}`}
            >
              <div className="sm-concierge-bubble">
                {m.content ? <MarkdownText text={m.content} /> : (m.streaming ? <span className="sm-concierge-typing"><span /><span /><span /></span> : null)}
              </div>
              {m.role === 'assistant' && !m.streaming && m.content && i > 0 && (
                <div className="sm-concierge-feedback" data-testid={`concierge-feedback-${i}`}>
                  <button
                    type="button"
                    className={`sm-concierge-feedback-btn ${m.rating === 'up' ? 'active' : ''}`}
                    onClick={() => sendFeedback(i, 'up')}
                    aria-label="Buena respuesta"
                    title="Buena respuesta"
                    disabled={!!m.rating}
                    data-testid={`concierge-feedback-up-${i}`}
                  >
                    <span className="icon-font">thumb_up</span>
                  </button>
                  <button
                    type="button"
                    className={`sm-concierge-feedback-btn ${m.rating === 'down' ? 'active' : ''}`}
                    onClick={() => sendFeedback(i, 'down')}
                    aria-label="Mejorable"
                    title="Mejorable"
                    disabled={!!m.rating}
                    data-testid={`concierge-feedback-down-${i}`}
                  >
                    <span className="icon-font">thumb_down</span>
                  </button>
                </div>
              )}
            </div>
          ))}
          {hasMoreBelow && (
            <button
              type="button"
              className="sm-concierge-more"
              onClick={scrollToBottom}
              aria-label="Ver más abajo"
              data-testid="concierge-scroll-more"
            >
              <span className="icon-font">expand_more</span>
            </button>
          )}
        </div>

        {(() => {
          // Show quick reply chips from the LATEST assistant message that has them.
          if (busy || messages.length === 0) return null;
          let lastAssistantWithQR = null;
          for (let i = messages.length - 1; i >= 0; i--) {
            const m = messages[i];
            if (m.role === 'assistant' && !m.streaming && Array.isArray(m.quickReplies) && m.quickReplies.length > 0) {
              lastAssistantWithQR = m;
              break;
            }
            if (m.role === 'user') break; // user already replied, no chips
          }
          if (!lastAssistantWithQR) return null;
          return (
            <div className="sm-concierge-suggestions" data-testid="concierge-suggestions">
              {lastAssistantWithQR.quickReplies.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => sendMessage(s)}
                  data-testid={`concierge-suggest-${s.slice(0, 12).replace(/\s+/g, '-')}`}
                >
                  {s}
                </button>
              ))}
            </div>
          );
        })()}

        <footer className="sm-concierge-foot">
          {leadSent && (
            <div className="sm-concierge-leadbadge" data-testid="concierge-lead-badge">
              <span className="icon-font">check_circle</span>
              Tus datos están con nosotros · un asesor te contacta pronto
            </div>
          )}
          <div className="sm-concierge-input">
            <textarea
              rows={1}
              placeholder={busy ? 'Mónica está escribiendo…' : 'Escribe tu mensaje…'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={busy}
              data-testid="concierge-input"
            />
            <button
              type="button"
              onClick={() => sendMessage(input)}
              disabled={busy || !input.trim()}
              aria-label="Enviar"
              data-testid="concierge-send"
            >
              <span className="icon-font">send</span>
            </button>
          </div>
          <div className="sm-concierge-disclaim">Powered by IA · Las respuestas son orientativas. Para precios y disponibilidad finales contacta a un asesor.</div>
        </footer>
      </div>
    </>
  );
}
